import { Injectable, inject } from "@angular/core";
import { ReplaySubject, Subject } from "rxjs";
import { MainService } from "core/main.service";
import { takeUntil } from "rxjs/operators";

export enum Size {
	XXSMALL = "xxsmall",
	XSMALL = "xsmall",
	SMALL = "small",
	MEDIUM = "medium",
	LARGE = "large",
	XLARGE = "xlarge",
}

export interface Breakpoint {
	name: Size;
	minWidth: number;
	isMobile: boolean; // is tablet
	isSmall: boolean; // is mobile
}

@Injectable({ providedIn: "root" })
export class LayoutService {
	private sidesheetWidth = 540;

	private destroy$ = new Subject<boolean>();
	private matchedBreakpoints: Set<Breakpoint> = new Set<Breakpoint>();
	private listeners: { mediaQueryList: MediaQueryList; listener: (event: MediaQueryListEvent) => void }[] = [];

	private breakpoints: Breakpoint[] = [
		{ name: Size.XLARGE, minWidth: 1600, isMobile: false, isSmall: false },
		{ name: Size.LARGE, minWidth: 1440, isMobile: false, isSmall: false },
		{ name: Size.MEDIUM, minWidth: 1281, isMobile: false, isSmall: false },
		{ name: Size.SMALL, minWidth: 1024, isMobile: true, isSmall: false },
		{ name: Size.XSMALL, minWidth: 768, isMobile: true, isSmall: false },
		{ name: Size.XXSMALL, minWidth: 414, isMobile: true, isSmall: true },
	].sort((a, b) => b.minWidth - a.minWidth);

	size: ReplaySubject<Size> = new ReplaySubject<Size>(1);
	breakpoint: ReplaySubject<Breakpoint> = new ReplaySubject<Breakpoint>(1);

	constructor() {
		const mainService = inject(MainService);

		mainService.applicationState.isChatbotOpen.pipe(takeUntil(this.destroy$)).subscribe((isSidesheetOpen) => {
			this.removeListeners();
			this.addListeners(isSidesheetOpen ? this.sidesheetWidth : 0);
		});

		//this.addListeners(0);
	}

	isAtLeast(breakpoint: Size): boolean {
		const largestBreakpoint = this.getLargestBreakpoint();
		if (!largestBreakpoint) {
			return false;
		}

		const requestedBreakpoint = this.breakpoints.find((bp) => bp.name === breakpoint);
		if (!requestedBreakpoint) {
			return false;
		}

		return largestBreakpoint.minWidth >= requestedBreakpoint.minWidth;
	}

	getMinWidth(breakpoint: Size): number | undefined {
		const requestedBreakpoint = this.breakpoints.find((bp) => bp.name === breakpoint);
		return requestedBreakpoint?.minWidth;
	}

	private addListeners(widthCorrection?: number): void {
		this.breakpoints.forEach((breakpoint) => {
			const minWidth = widthCorrection ? breakpoint.minWidth + widthCorrection : breakpoint.minWidth;
			const mediaQueryList = window.matchMedia(`(min-width: ${minWidth}px)`);
			const listener = (event: MediaQueryListEvent): void => this.onBreakpoint(breakpoint, event.matches);

			mediaQueryList.addEventListener("change", listener);
			this.listeners.push({ mediaQueryList: mediaQueryList, listener });

			if (mediaQueryList.matches) {
				this.matchedBreakpoints.add(breakpoint);
			}
		});

		this.notifySizeChange();
	}

	private removeListeners(): void {
		this.listeners.forEach(({ mediaQueryList, listener }) => {
			mediaQueryList.removeEventListener("change", listener);
		});
		this.listeners = [];
		this.matchedBreakpoints.clear();
	}

	private getLargestBreakpoint(): Breakpoint {
		if (this.matchedBreakpoints.size === 0) {
			return this.breakpoints[this.breakpoints.length - 1];
		}

		return [...this.matchedBreakpoints].reduce((largest, current) =>
			current.minWidth > largest.minWidth ? current : largest,
		);
	}

	private onBreakpoint(breakpoint: Breakpoint, matches: boolean): void {
		if (matches) {
			this.matchedBreakpoints.add(breakpoint);
		} else {
			this.matchedBreakpoints.delete(breakpoint);
		}

		this.notifySizeChange();
	}

	private notifySizeChange(): void {
		const largestBreakpoint = this.getLargestBreakpoint();
		this.size.next(largestBreakpoint.name);
		this.breakpoint.next(largestBreakpoint);
	}
}
